import { externalIntegrationsRepositorySingleton } from '@warehouse/object-browser/infra';
import { ExternalIntegrationTab } from '@warehouse/object-browser/core';
import { localStorageRepositorySingleton } from '@warehouse/shared/util';
import { QuickBaseAuthService } from './quickbase-auth.service';
import { canAutoBounce, clearAutoBounce, markAutoBounce } from './quickbase-auth-complete';
import { QB_AUTH_TIMEOUT_MS, QuickbaseAuthFlow } from '../quickbase-types';

type QuickBaseAuthConfig = {
	domain: string;
	clientId: string;
	audience: string;
};

type QuickbaseReturnConfig = {
	serviceURL: string;
	appID: string;
	authReturnPage: string;
	auth0Domain?: string;
	auth0ClientId?: string;
};

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 600;
const TAB_INTEGRATION_IDS = ['nxTension', 'nxOrders', 'nxInventory'];

export class QuickBaseAuthServiceImpl implements QuickBaseAuthService {
	constructor(private readonly navigate: (url: string) => void = (url) => window.location.assign(url)) {}

	getAuthFlow(): QuickbaseAuthFlow {
		return this.getReturnConfig() ? 'verified' : 'legacy';
	}

	canAutoRecover(): boolean {
		if (!this.getReturnConfig()) return false;
		return canAutoBounce(localStorageRepositorySingleton.get());
	}

	clearRecoveryCooldown(): void {
		clearAutoBounce(localStorageRepositorySingleton.get());
	}

	buildEmbedProbeUrl(deepUrl: string): string | null {
		const config = this.getReturnConfig();
		if (!config) return null;

		return `${this.returnPageUrl(config)}&target=${encodeURIComponent(deepUrl)}`;
	}

	async authenticate(): Promise<boolean> {
		const returnConfig = this.getReturnConfig();
		if (returnConfig) {
			return this.authenticateWithRedirect(returnConfig);
		}
		return this.authenticateLegacy();
	}

	/** The return page, told where Warehouse is (`origin`) and which page the user was on (`back`). */
	private returnPageUrl(config: QuickbaseReturnConfig): string {
		const back = `${window.location.pathname}${window.location.search}${window.location.hash}`;
		return `${config.serviceURL}db/${config.appID}?a=dbpage&pagename=${config.authReturnPage}&origin=${encodeURIComponent(window.location.origin)}&back=${encodeURIComponent(back)}`;
	}

	// IdP-initiated entry so Auth0 reuses the Warehouse session; Quickbase's own SP-initiated request
	// sets ForceAuthn and would re-prompt. Falls back to the return page directly if no Auth0 client.
	private ssoEntryUrl(config: QuickbaseReturnConfig, landingUrl: string): string {
		if (config.auth0Domain && config.auth0ClientId) {
			return `https://${config.auth0Domain}/samlp/${config.auth0ClientId}?RelayState=${encodeURIComponent(landingUrl)}`;
		}
		return landingUrl;
	}

	private firstConfiguredTab(predicate: (tab: ExternalIntegrationTab) => boolean): ExternalIntegrationTab | undefined {
		const repository = externalIntegrationsRepositorySingleton.get();
		return TAB_INTEGRATION_IDS.map((id) => repository.getExternalTab(id)).find(
			(tab): tab is ExternalIntegrationTab => !!tab && predicate(tab),
		);
	}

	private getReturnConfig(): QuickbaseReturnConfig | undefined {
		const tab = this.firstConfiguredTab((integration) =>
			Boolean(integration.params?.authReturnPage && integration.params.appID),
		);
		if (tab) {
			return {
				serviceURL: tab.serviceURL,
				appID: tab.params.appID,
				authReturnPage: tab.params.authReturnPage!,
				auth0Domain: tab.params.auth0Domain,
				auth0ClientId: tab.params.auth0ClientId,
			};
		}

		const navBar = externalIntegrationsRepositorySingleton.get().getNavBar();
		const navParams = navBar?.params;
		if (!navBar || !navParams?.authReturnPage) return undefined;

		const appID = [
			navParams.dashboard,
			navParams.utilities,
			navParams.admin,
			navParams.orders,
			navParams.inventory,
		].find((params) => params?.appID)?.appID;
		if (!appID) return undefined;

		return {
			serviceURL: navBar.serviceURL,
			appID,
			authReturnPage: navParams.authReturnPage,
			auth0Domain: navParams.auth0Domain,
			auth0ClientId: navParams.auth0ClientId,
		};
	}

	private authenticateWithRedirect(config: QuickbaseReturnConfig): Promise<boolean> {
		markAutoBounce(localStorageRepositorySingleton.get());
		this.navigate(this.ssoEntryUrl(config, this.returnPageUrl(config)));

		// The redirect normally unloads the page before this settles. If navigation is silently blocked
		// (CSP, extension, org policy), resolve false after a bound so the caller can fall back instead of
		// hanging, mirroring the legacy popup timeout.
		return new Promise<boolean>((resolve) => {
			setTimeout(() => resolve(false), QB_AUTH_TIMEOUT_MS);
		});
	}

	private getAuth0Config(): QuickBaseAuthConfig {
		const auth0Config =
			this.firstConfiguredTab(() => true) || externalIntegrationsRepositorySingleton.get().getNavBar();
		if (!auth0Config || !auth0Config.params?.auth0Domain || !auth0Config.params?.auth0ClientId) {
			throw new Error('Auth0 configuration not found');
		}

		return {
			domain: auth0Config.params.auth0Domain,
			clientId: auth0Config.params.auth0ClientId,
			audience: auth0Config.serviceURL,
		};
	}

	private authenticateLegacy(): Promise<boolean> {
		return new Promise((resolve) => {
			try {
				const config = this.getAuth0Config();

				const authUrl = `https://${config.domain}/samlp/${config.clientId}`;
				const left = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
				const top = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);

				const popup = window.open(
					authUrl,
					'quickbase-auth-popup',
					`width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`,
				);

				if (!popup) {
					resolve(false);
					return;
				}

				const checkClosed = setInterval(() => {
					if (popup.closed) {
						clearInterval(checkClosed);
						// For SAML flow, we assume success when popup closes
						// The actual authentication state will be verified when iframe loads
						resolve(true);
					}
				}, 1000);

				// Timeout after 5 minutes
				setTimeout(() => {
					if (!popup.closed) {
						popup.close();
						clearInterval(checkClosed);
						resolve(false);
					}
				}, QB_AUTH_TIMEOUT_MS);
			} catch {
				resolve(false);
			}
		});
	}
}
